import { CalendarMonth } from "@tento-ui/components/calendar-month/calendar-month";
import { CalendarMonthCell } from "@tento-ui/components/calendar-month/calendar-month-cell";
import { CalendarMonthGesture } from "@tento-ui/components/calendar-month/calendar-month-gesture";
import type { Option, RowLocalId } from "@tento-lona/sheets";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import type { SheetRow, LonaSdk } from "@tento-lona";
import { NaiveDate, TimezoneRegion, Ym1Like } from "@tento-chrono";
import type {
  GestureCoordinatorLike,
  GestureSystem,
} from "@tento-lona/sheets-ui/sheet/gestures/gesture-system.ts";
import { Invalidation } from "@tento-lona/sheets";
import type {
  CalendarMonthLayoutDelegate,
  CalendarMonthPendingEvent,
} from "@tento-lona/sheets-ui/sheet/calendar-month-layout-delegate.ts";
import type { CalendarMonthSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-month-subcolumn.ts";
import { calculateStartPadding } from "./calendar-month-utils.ts";
import { CalendarPreferences } from "@tento-lona";
import { CrossSystemDismissalGuard } from "@tento-lona/sheets-ui/sheet/gestures/dismissal-guard.ts";
import { Tracing } from "@tento-core/tracing";

/**
 * Calendar-month widget host. Pure structural layout — owns the
 * `CalendarMonth` widget + gesture manager + the cross-system
 * dismissal guard, but knows nothing about events. Renderer plugins
 * provide painting via the `CalendarMonthSubcolumn` protocol; the
 * layout discovers them via `contentProviders.calendarMonth` and
 * passes them through `bind(props)`.
 */
export class CalendarMonthHost implements GestureSystem {
  readonly $calendar: CalendarMonth = CalendarMonth.make();
  readonly gestureManager: CalendarMonthGesture.Manager<CalendarMonthGesture.Info>;
  #currentProps: Option<CalendarMonthHost.Props> = null;
  #currentHandle: Option<SheetDelegate> = null;
  #currentRowId: Option<RowLocalId> = null;
  #loadingVersion = 0;
  /**
   * Pending (in-flight) new-event state. Stored on the layout so the
   * `CalendarMonthActions.Delegate` getter/setter (used by the
   * renderer's actions for create-flow + reshow) can read/write a
   * single shared location.
   */
  #pendingEvent: Option<CalendarMonthPendingEvent> = null;
  /** Subcolumns from contentProviders.calendarMonth discovery; reset per bind. */
  #subcolumns: readonly CalendarMonthSubcolumn[] = [];

  readonly #dismissalGuard: CrossSystemDismissalGuard;
  #unsubscribeStore: () => void;

  /**
   * The layout's bound row (the calendar-month row itself). Set via
   * `bind(props)`. Used to discover the event-capable child row via
   * `findRowWithFacade(EVENTS_FACADE)` for create + existing-event
   * mutations (Phase E.0b) and the `EVENTS_FACADE` for reads (Phase F.1).
   */
  #boundRow: Option<SheetRow.Joined.Handle> = null;
  readonly #coordinator: GestureCoordinatorLike;
  readonly #calendarPreferences: CalendarPreferences;

  /**
   * The delegate the renderer's `CalendarMonthActions` reads to learn
   * per-bind state (handle, rowId, boundRow, tz, month, pendingEvent)
   * + trigger a re-bind. Stable across binds; values change as props
   * change. Exposed publicly so the layout can build the
   * `CalendarMonthSubcolumn.Host` it passes to discovered providers.
   */
  readonly delegate: CalendarMonthLayoutDelegate;

  constructor(deps: CalendarMonthHost.Deps) {
    const { sdk, coordinator } = deps;
    this.#coordinator = coordinator;
    this.#calendarPreferences = sdk.preferences.calendar();
    this.#dismissalGuard = new CrossSystemDismissalGuard(this, coordinator.globalState);
    coordinator.register(this);
    coordinator.registerClickOutside(this.$calendar);

    // Re-bind the month view on any event-level change — TimepointsChanged
    // (partial) and ViewportsInvalidated (full-day) both match the
    // "calendars.events" prefix.
    this.#unsubscribeStore = sdk.invalidation.subscribe((event) => {
      if (Invalidation.matches(event, "calendars.events")) {
        this.#rebindCalendar();
      }
    });

    this.delegate = {
      getHandle: () => this.#currentHandle,
      getRowId: () => this.#currentRowId,
      getBoundRow: () => this.#boundRow,
      getTz: () => this.#currentProps?.tz ?? null,
      getMonth: () => this.#currentProps?.month ?? null,
      getPendingEvent: () => this.#pendingEvent,
      setPendingEvent: (pending) => {
        this.#pendingEvent = pending;
      },
      rebindCalendar: () => this.#rebindCalendar(),
    };

    this.gestureManager = new CalendarMonthGesture.Manager<CalendarMonthGesture.Info>()
      .addPointerMoveCallback("highlight", (info) => {
        this.$calendar.highlightCell(info.date);
      })
      .addGestureChangeCallback("highlight", (_, context) => {
        if (context === "pointer-up" || context === "pointer-up-empty") {
          this.$calendar.highlightCell(null);
        }
      });

    this.$calendar.setCallbacks(this.gestureManager.asCalendarMonthDelegate());
  }

  addGesture(gesture: CalendarMonthGesture<CalendarMonthGesture.Info>): void {
    this.gestureManager.addGesture(gesture);
  }

  #rebindCalendar(): void {
    if (!this.#currentProps) return;
    // Data-change repaint from cache — NO fetch. A rebind fires only on
    // event-data changes (already in `canonicalCells`); re-fetching would
    // re-ingest stale pre-write server state and clobber an in-flight
    // optimistic edit (the move "snap-back"). Structural / range changes go
    // through `bind()`, which fetches. Re-bind (cells + spans), not just
    // updateSpans: timed-event chips live in cell content, so a spans-only
    // repaint left them stale on every event-data settle.
    this.#bindCalendar(this.#currentProps, this.#cachedSpans());
  }

  // === Cross-system gesture coordination ===

  /**
   * GestureSystem interface: close all menus/details in this system.
   * Called by coordinator when another system takes precedence. Each
   * subcolumn (renderer-provided) dismisses whatever popovers/menus
   * it owns.
   */
  closeAllMenus(): void {
    for (const subcolumn of this.#subcolumns) {
      subcolumn.dismissAll?.();
    }
  }

  /**
   * Clean up resources when this calendar is no longer needed.
   */
  dispose(): void {
    this.#unsubscribeStore();
  }

  shouldBlockForCrossSystemDismissal(isPointerDown: boolean): boolean {
    return this.#dismissalGuard.shouldBlock(isPointerDown, null);
  }

  bind(props: CalendarMonthHost.Props): CalendarMonth {
    this.#currentProps = props;
    this.#currentHandle = props.handle;
    this.#currentRowId = props.rowId;
    this.#boundRow = props.row;
    this.#subcolumns = props.subcolumns;
    const version = ++this.#loadingVersion;
    // Declarative: paint immediately from cache (no await) so the lane
    // isn't emptied for a frame and an in-place edit paints at its CURRENT
    // position — not a stale snapshot that flashes the prior location.
    this.#bindCalendar(props, this.#cachedSpans());
    // Imperative: load any unloaded range in the background, then repaint.
    void this.#fetchSpans(version);
    return this.$calendar;
  }

  /**
   * The 42-cell grid range for a month — includes the prev/next-month
   * padding days that fill the 6×7 grid.
   */
  #gridRange(month: Ym1Like): NaiveDate.Range {
    const firstDay = NaiveDate.fromYmd1(month.yr, month.mth, 1).exp();
    const startOfWeek = this.#calendarPreferences.startOfWeek();
    const gridStart = firstDay.addDays(
      -calculateStartPadding(firstDay.dayOfWeek, startOfWeek),
    );
    return new NaiveDate.Range(gridStart, gridStart.addDays(41));
  }

  /**
   * Spans built from the ALREADY-CACHED canonical cells — zero I/O. The
   * single source for both the declarative seed (`bind`) and data-change
   * repaints (`rebindCalendar`).
   */
  #cachedSpans(): CalendarMonth.Span[] {
    if (!this.#currentProps) return [];
    const range = this.#gridRange(this.#currentProps.month);
    return this.#subcolumns.flatMap((s) => s.provideSpansNow(range));
  }

  /**
   * The ONLY network I/O on the calendar path: load any unloaded range
   * from the server, then repaint from the now-populated cache. Kept out
   * of `bind`/`rebindCalendar` so those stay declarative (cache → render).
   * `version` guards against an out-of-order resolve from a superseded bind.
   */
  async #fetchSpans(version: number): Promise<void> {
    const props = this.#currentProps;
    if (!props) return;
    const range = this.#gridRange(props.month);
    try {
      const arrays = await Promise.all(
        this.#subcolumns.map((s) => s.provideSpans(range)),
      );
      if (this.#loadingVersion !== version) return;
      // Repaint cell CONTENT, not just spans. Timed-event chips are painted by
      // bindCell (cell content), which on the first-load bind ran against an
      // empty facade. `updateSpans` is drag-optimized and BYPASSES cell
      // re-rendering — it only repaints all-day bars — so timed events stayed
      // missing until a later full bind (scroll/nav). Re-bind from the
      // now-populated cache (no fetch).
      this.#bindCalendar(props, arrays.flat());
    } catch (err) {
      // Surface loudly via Tracing instead of being swallowed by `void` —
      // silent provideSpans failures are a known "month renders but events
      // are missing" footgun.
      Tracing.span("calendar-month/spans-failed", (s) => {
        s.record("rowId", this.#currentRowId?.toString() ?? "null");
        s.record("rangeStart", range.start.toString());
        s.record("rangeEnd", range.end.toString());
        s.record("error", err instanceof Error ? err.message : String(err));
        s.record("stack", err instanceof Error ? (err.stack ?? "") : "");
        s.setLevel("error");
      });
      throw err;
    }
  }

  #bindCalendar(
    props: CalendarMonthHost.Props,
    spans: import("@tento-ui/components/calendar-month/calendar-month").CalendarMonth.Span[],
  ): void {
    const { month, today, tz, height } = props;

    this.$calendar.bind({
      month,
      bind: (cell, date, _isCurrentMonth) => {
        this.#bindCell(cell, date, today, tz);
      },
      cellHeight: height / 6,
      options: {
        startOfWeek: this.#calendarPreferences.startOfWeek(),
        showLeftBorder: false,
        today,
        spans,
        spanLaneGap: 2,
        spanLaneHeight: 22,
      },
    });

    this.$calendar.assignStyles({
      height: height + "px",
      "--span-lane-height": "22px",
    });
  }

  #bindCell(
    $cell: CalendarMonthCell,
    date: NaiveDate,
    today: NaiveDate,
    tz: TimezoneRegion,
  ): void {
    $cell.bindDate(date, today);
    $cell.clearChildren();

    // Synchronous seed from already-loaded data — the cell paints in the
    // same frame as the layout bind, with no empty-then-fill flicker
    // when data is already cached AND no stale paint across the async
    // load. Range loading is driven once per bind by `provideSpans` at
    // the layout level; when it completes, the existing
    // `"calendars.events"` invalidation triggers a re-bind and the seed
    // re-paints with fresh data. Mirrors the span-seed path.
    for (const subcolumn of this.#subcolumns) {
      subcolumn.provideCellContentNow($cell, date, tz);
    }
  }
}

/** Types that go with `CalendarMonthHost`: `Deps`, `Props`. */
export namespace CalendarMonthHost {
  /**
   * Pure widget-positioning dependencies. Event-domain concerns
   * (popovers, gestures, mutations, painting) live entirely on the
   * renderer side via the `CalendarMonthSubcolumn` protocol —
   * `CalendarMonthHost` no longer constructs `CalendarMonthActions`.
   */
  export interface Deps {
    sdk: LonaSdk.Client;
    coordinator: GestureCoordinatorLike;
  }

  export interface Props {
    month: Ym1Like;
    today: NaiveDate;
    tz: TimezoneRegion;
    height: number;
    handle: SheetDelegate;
    rowId: RowLocalId;
    /**
     * The layout's bound row. Used by renderer subcolumns to walk
     * `row.findRowWithFacade(EVENTS_FACADE)` for create + existing-event
     * mutations.
     */
    row: SheetRow.Joined.Handle;
    /**
     * Renderer-provided subcolumns. Discovered by `GridLayoutPlugin`
     * via `contentProviders.calendarMonth` and threaded through here.
     * Empty when no renderer claims the layout's children.
     */
    subcolumns: readonly CalendarMonthSubcolumn[];
  }
}
